export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly correlationId?: string;
  readonly requestId?: string;

  constructor(status: number, shape: ApiErrorShape, requestId?: string) {
    super(shape.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = shape.code;
    this.details = shape.details;
    this.correlationId = shape.correlationId;
    this.requestId = requestId;
  }

  get isUnauthorized() { return this.status === 401; }
  get isForbidden() { return this.status === 403; }
  get isNotFound() { return this.status === 404; }
  get isRetryable() { return this.status === 0 || this.status === 429 || this.status >= 500; }
}

export function normalizeApiError(
  status: number,
  payload: unknown,
  fallback: ApiErrorShape,
  requestId?: string,
): ApiError {
  const candidate = typeof payload === 'object' && payload !== null && 'error' in payload
    ? (payload as { error?: unknown }).error
    : payload;
  const shape = typeof candidate === 'object'
    && candidate !== null
    && 'code' in candidate
    && typeof candidate.code === 'string'
    && 'message' in candidate
    && typeof candidate.message === 'string'
    ? candidate as ApiErrorShape
    : fallback;
  // A rejected schema arrives as the generic "Validation failed." plus a
  // `details` array holding the message that actually explains the problem
  // (see ZodValidationPipe and HttpExceptionFilter, which both emit this
  // shape). Showing only the generic line tells the person nothing they can
  // act on — a shopper mistyping a tracking token saw exactly
  // "Validation failed." Promote the specific reason so every form in both
  // apps explains itself.
  if (shape.message === 'Validation failed.' && Array.isArray(shape.details) && shape.details.length > 0) {
    const specific = shape.details
      .map((detail) => (typeof detail === 'object' && detail !== null && typeof (detail as { message?: unknown }).message === 'string'
        ? (detail as { message: string }).message
        : null))
      .filter((message): message is string => !!message);
    if (specific.length > 0) {
      return new ApiError(status, { ...shape, message: specific.join(' ') }, requestId);
    }
  }
  return new ApiError(status, shape, requestId);
}

export type CredentialPolicy = 'omit' | 'include' | 'same-origin';

export interface ApiClientOptions {
  baseUrl: string;
  credentials: CredentialPolicy;
  getHeaders?: () => HeadersInit | Promise<HeadersInit>;
  timeoutMs?: number;
  fetcher?: typeof fetch;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'credentials'> {
  body?: unknown;
  timeoutMs?: number;
  unwrapEnvelope?: boolean;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
  upload<T>(path: string, form: FormData, options?: RequestOptions): Promise<T>;
  raw<T>(path: string, options?: RequestOptions): Promise<T>;
  text(path: string, options?: RequestOptions): Promise<string>;
  url(path: string): string;
}

function normalizeBaseUrl(value: string): string {
  const trimmed = value.trim();
  return trimmed ? trimmed.replace(/\/+$/, '') : '';
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function isDataEnvelope(value: unknown): value is { data: unknown } {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'data' in value;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  if (!options || !('credentials' in options)) {
    throw new TypeError('createApiClient requires an explicit credentials policy.');
  }
  const baseUrl = normalizeBaseUrl(options.baseUrl);
  const defaultTimeout = options.timeoutMs ?? 30_000;

  const request = async <T>(path: string, requestOptions: RequestOptions = {}, responseKind: 'json' | 'text' = 'json'): Promise<T> => {
    const { body, timeoutMs = defaultTimeout, signal: callerSignal, headers, unwrapEnvelope = true, ...init } = requestOptions;
    const controller = new AbortController();
    let timedOut = false;
    const timer = timeoutMs > 0 ? setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs) : undefined;
    const onCallerAbort = () => controller.abort(callerSignal?.reason);
    if (callerSignal?.aborted) onCallerAbort();
    else callerSignal?.addEventListener('abort', onCallerAbort, { once: true });

    const formData = typeof FormData !== 'undefined' && body instanceof FormData;
    const mergedHeaders = new Headers(await options.getHeaders?.());
    new Headers(headers).forEach((value, key) => mergedHeaders.set(key, value));
    if (body !== undefined && !formData && !mergedHeaders.has('content-type')) mergedHeaders.set('content-type', 'application/json');

    let response: Response;
    let text: string;
    try {
      const fetcher = options.fetcher ?? fetch;
      response = await fetcher(`${baseUrl}${normalizePath(path)}`, {
        ...init,
        credentials: options.credentials,
        signal: controller.signal,
        headers: mergedHeaders,
        body: body === undefined ? undefined : formData ? body as FormData : JSON.stringify(body),
      });
      text = await response.text();
    } catch (cause) {
      if (timedOut) throw new ApiError(0, { code: 'REQUEST_TIMEOUT', message: 'The request timed out. Please try again.' });
      if (callerSignal?.aborted) throw new ApiError(0, { code: 'REQUEST_ABORTED', message: 'The request was cancelled.' });
      throw new ApiError(0, {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the Enshield API. Check your connection and try again.',
        details: cause instanceof Error ? cause.message : String(cause),
      });
    } finally {
      if (timer) clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    }

    const requestId = response.headers.get('x-request-id') ?? undefined;
    if (response.ok && responseKind === 'text') return text as T;
    let payload: unknown = {};
    if (text) {
      try { payload = JSON.parse(text); }
      catch { throw new ApiError(response.status, { code: 'INVALID_RESPONSE', message: 'The server returned an unexpected response.' }, requestId); }
    }
    if (!response.ok) {
      throw normalizeApiError(
        response.status,
        payload,
        { code: 'UNKNOWN_ERROR', message: 'Request failed.' },
        requestId,
      );
    }
    return (unwrapEnvelope && isDataEnvelope(payload) ? payload.data : payload) as T;
  };

  return {
    get: (path, requestOptions) => request(path, { ...requestOptions, method: 'GET' }),
    post: (path, body, requestOptions) => request(path, { ...requestOptions, method: 'POST', body }),
    patch: (path, body, requestOptions) => request(path, { ...requestOptions, method: 'PATCH', body }),
    put: (path, body, requestOptions) => request(path, { ...requestOptions, method: 'PUT', body }),
    delete: (path, requestOptions) => request(path, { ...requestOptions, method: 'DELETE' }),
    upload: (path, form, requestOptions) => request(path, { ...requestOptions, method: 'POST', body: form, timeoutMs: requestOptions?.timeoutMs ?? 120_000 }),
    raw: (path, requestOptions) => request(path, { ...requestOptions, unwrapEnvelope: false }),
    text: (path, requestOptions) => request<string>(path, { ...requestOptions, method: 'GET' }, 'text'),
    url: (path) => `${baseUrl}${normalizePath(path)}`,
  };
}
